import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import SingleWidgetAnswerRenderer from '../Widgets/SingleWidgetAnswerRenderer';
import JsonTableRenderer from './JsonTableRenderer';
import { parseJsonValueWithRepair } from '../../Utils/jsonValueUtils';

const normalizeTypeToAnswerFormat = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'html-with-js') {
    return 'html-with-js';
  }
  if (normalized === 'image') {
    return 'image';
  }
  return 'plaintext';
};
const looksLikeHtmlPayload = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return false;
  }
  return /<\s*(html|body|div|span|table|thead|tbody|tr|td|th|ul|ol|li|p|h[1-6]|svg|canvas|script|style|iframe)\b/i.test(text);
};
const isHtmlAnswerType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'html-with-js' || normalized === 'html' || normalized === 'interactive-html';
};
const parseJsonWithNesting = (value) => {
  return parseJsonValueWithRepair(value, 2);
};

const parseWidgetAnswerPayload = (value, depth = 0) => {
  if (depth > 2) {
    return null;
  }
  if (value === null || value === undefined) {
    return null;
  }
  let parsed = value;
  if (typeof value === 'string') {
    parsed = parseJsonWithNesting(value);
    if (parsed === null) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const normalized = { ...parsed };
  if (!normalized.ui_template && normalized.uiTemplate) {
    normalized.ui_template = normalized.uiTemplate;
  }
  if (normalized.data_json === undefined && normalized.data !== undefined) {
    normalized.data_json = normalized.data;
  }

  if (!Object.prototype.hasOwnProperty.call(normalized, 'answer')) {
    if (Object.prototype.hasOwnProperty.call(normalized, 'response')) {
      const nested = parseWidgetAnswerPayload(normalized.response, depth + 1);
      if (nested) {
        return nested;
      }
    }

    const hasTemplate = typeof normalized.ui_template === 'string' && normalized.ui_template.trim();
    if (hasTemplate) {
      let dataPayload = '[]';
      if (normalized.data_json !== undefined && normalized.data_json !== null) {
        if (typeof normalized.data_json === 'string') {
          dataPayload = normalized.data_json.trim() || '[]';
        } else {
          try {
            dataPayload = JSON.stringify(normalized.data_json);
          } catch (e) {
            dataPayload = '[]';
          }
        }
      }

      const materialized = normalized.ui_template.replace(
        /const\s+MY_DATA\s*=\s*\[\s*\]\s*;?/i,
        `const MY_DATA = ${dataPayload};`
      );
      return {
        answer: materialized,
        type: 'html-with-js'
      };
    }
    return null;
  }

  return {
    answer: normalized.answer == null ? '' : (typeof normalized.answer === 'string' ? normalized.answer : JSON.stringify(normalized.answer)),
    type: (
      isHtmlAnswerType(normalized.type)
      || isHtmlAnswerType(normalized.answerFormat)
      || looksLikeHtmlPayload(normalized.answer)
    ) ? 'html-with-js' : undefined
  };
};

export const isLikelyImageData = (tagType) => {
  return normalizeTypeToAnswerFormat(tagType) === 'image';
};

const resolveImageSrc = (value) => {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) {
    return '';
  }
  if (raw.startsWith('data:image/')) {
    return raw;
  }
  return `data:image/png;base64,${raw}`;
};

const parseJsonObjectOrArray = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  try {
    const parsed = parseJsonValueWithRepair(raw, 2);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch (e) {
    // Keep the renderer's non-throwing behavior for unexpected parser errors.
  }

  return null;
};

export const resolveUnifiedAnswerPreview = (tag, replyValue) => {
  const renderType = normalizeTypeToAnswerFormat(tag?.type) || 'plaintext';
  if (renderType === 'image') {
    return null;
  }

  const payload = parseWidgetAnswerPayload(replyValue);
  if (payload) {
    const resolvedType = payload.type || (renderType === 'plaintext' ? 'text' : renderType);
    return {
      answer: payload.answer,
      type: resolvedType
    };
  }

  const raw = replyValue == null ? '' : String(replyValue);
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return null;
    }
  } catch (e) {
    // Not JSON object/array; continue.
  }

  return {
    answer: raw,
    type: looksLikeHtmlPayload(raw) ? 'html-with-js' : (renderType === 'plaintext' ? 'text' : renderType)
  };
};

const UnifiedAnswerRenderer = ({
  tag,
  value,
  resolvedAnswer = null,
  widgetId,
  height = null,
  onImageClick = null,
  shouldAutoExpand = false,
  disableInternalScroll = false
}) => {
  const renderType = useMemo(
    () => normalizeTypeToAnswerFormat(tag?.type) || 'plaintext',
    [tag?.type]
  );
  const rawValue = value == null ? '' : String(value);
  const computedAnswer = useMemo(
    () => resolveUnifiedAnswerPreview(tag, value),
    [tag, value]
  );
  const answerToRender = resolvedAnswer || computedAnswer;
  const jsonData = useMemo(
    () => (renderType === 'plaintext' ? parseJsonObjectOrArray(value) : null),
    [renderType, value]
  );

  const boundedHeight = Number.isFinite(height) && height > 0 ? height : null;
  const previewMaxHeight = boundedHeight || 'calc(100vh - 220px)';

  if (renderType === 'image') {
    const imageSrc = resolveImageSrc(value);
    if (!imageSrc) {
      return (
        <Box sx={{ width: '100%', p: 1 }}>
          <Typography variant="caption" sx={{ color: '#888' }}>
            No image data
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', height: boundedHeight || 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img
          src={imageSrc}
          alt="Tag preview"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: '8px',
            border: '1px solid rgba(87,87,176,0.18)',
            cursor: onImageClick ? 'pointer' : 'default',
            objectFit: 'contain'
          }}
          onClick={() => {
            if (onImageClick) {
              onImageClick(imageSrc);
            }
          }}
        />
      </Box>
    );
  }

  if (renderType === 'plaintext' && jsonData && !answerToRender) {
    return (
      <Box sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        ...(disableInternalScroll
          ? { overflow: 'visible' }
          : { maxHeight: previewMaxHeight, overflowY: 'auto', overflowX: 'hidden' })
      }}>
        <JsonTableRenderer
          data={jsonData}
          maxHeight={null}
          maxItems={Infinity}
          showIndex={Array.isArray(jsonData)}
          compact={false}
        />
      </Box>
    );
  }

  if (!answerToRender && !rawValue.trim()) {
    return null;
  }

  const fallbackAnswer = answerToRender || {
    answer: rawValue,
    type: renderType === 'plaintext' ? 'text' : renderType
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', ...(boundedHeight ? { height: boundedHeight, overflow: 'auto' } : { overflow: 'visible' }) }}>
      <SingleWidgetAnswerRenderer
        answer={fallbackAnswer}
        widgetId={widgetId}
        isMaximized={false}
        shouldAutoExpand={shouldAutoExpand}
      />
    </Box>
  );
};

export default UnifiedAnswerRenderer;
